/**
 * This component is going to be a AnonymousOrderDetailStatus form where buyer can check the status of their order
 *
 * @module views/components/AnonymousOrderDetailStatus
 * @memberof -Common
 */
import './AnonymousOrderDetailStatus.scss';

import React, { useEffect } from 'react';

import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import Button from '@ulta/core/components/Button/Button';
import InputField from '@ulta/core/components/InputField/InputField';
import Text from '@ulta/core/components/Text/Text';
import useLoader from '@ulta/core/hooks/useLoader/useLoader';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import BlockMessage from '@ulta/components/BlockMessage/BlockMessage';

import { getIsButtonDisabled } from '@ulta/utils/Validations/Validations';

import * as utils from './AnonymousOrderDetailStatus';

/**
 * Represents a AnonymousOrderDetailStatus component
 *
 * @method
 * @param {AnonymousOrderDetailStatusProps} props - React properties passed from composition
 * @returns OrderStatus
 */
export const AnonymousOrderDetailStatus = function( props ){
  const {
    title,
    subTitle,
    email,
    orderNumber,
    buttonAction,
    messages,
    componentKey,
    id,
    loading,
    invokeMutation
  } = props;

  const [loader, showLoader, hideLoader] = useLoader( {} );

  useEffect( () => {
    loading ? showLoader() : hideLoader();
  }, [loading] );

  if( !email && !orderNumber ){
    return null;
  }

  const initialValues = { email: '', orderNumber: '' };
  const emailValidationMessage = email?.validations?.[0]?.message;
  const orderNumberValidationMessage = orderNumber?.validations?.[0]?.message;

  const validationSchema = Yup.object( {
    email: Yup.string().matches( email?.validations?.[0]?.regex, emailValidationMessage ).required( emailValidationMessage ),
    orderNumber: Yup.string().matches( orderNumber?.validations?.[0]?.regex, orderNumberValidationMessage ).required( orderNumberValidationMessage )
  } );

  const hasMessages = hasItems( messages );

  return (
    <div className='AnonymousOrderDetailStatus'>
      { loader }
      { title &&
        <div className='AnonymousOrderDetailStatus__title'>
          <Text textStyle='title-6'>
            { title }
          </Text>
        </div>
      }
      { subTitle &&
        <div className='AnonymousOrderDetailStatus__subTitle'>
          <Text
            textStyle='body-2'
            color='neutral-600'
          >
            { subTitle }
          </Text>
        </div>
      }
      { hasMessages &&
        messages?.map( ( data, index ) => (
          <div className='AnonymousOrderDetailStatus__message'
            key={ `${index}-${componentKey}` }
          >
            <BlockMessage
              message={ data.message }
              messageType={ data.icon }
              backgroundColor={ data.backgroundColor }
              borderColor={ data.borderColor }
              textColor={ data.textColor }
              dismissAction={ data.dismissAction }
              action1={ data.action1 }
              action1Style='link'
              action2={ data.action2 }
              ariaLiveType={ 'assertive' }
              role={ 'status' }
            />
          </div>
        ) ) }
      <Formik
        initialValues={ initialValues }
        validateOnMount
        enableReinitialize={ true }
        validationSchema={ validationSchema }
        onSubmit={ ( values ) => {
          utils.handleSubmit( { values, buttonAction, contentId: id }, { invokeMutation, showLoader } );
        } }
      >
        { ( fprops ) => (
          <Form>
            <div className='AnonymousOrderDetailStatus__email'>
              <InputField
                { ...spreadProps( { ...email } ) }
                autoComplete='email'
              />
            </div>
            <div className='AnonymousOrderDetailStatus__orderNumber'>
              <InputField
                { ...spreadProps( { ...orderNumber } ) }
                autoComplete='orderNumber'
              />
            </div>
            <Button
              type='submit'
              primary
              withHover
              fullWidth
              disabled={ getIsButtonDisabled( { isValid: fprops.isValid, dirty: fprops.dirty } ) }
              label={ buttonAction?.label }
              action={ buttonAction }
            />
          </Form>
        ) }
      </Formik>
    </div>
  );
};

/**
 * @function handleSubmit
 * @description - handles the submit event on the check button
 * @param {Object} methods - Passing methods as an argument for the mutation call
 * @param {String} data - Passing data as an argument
 */
export const handleSubmit = ( data, methods ) => {
  const { values, buttonAction, contentId } = data || {};
  const { invokeMutation, showLoader } = methods || {};
  const { graphql, params, customHeaders } = buttonAction || {};

  if( !graphql || !invokeMutation ){
    return;
  }

  showLoader();

  const moduleParams = {};
  Object.keys( values ).forEach( ( key ) => {
    moduleParams[key] = { name: key, value: values[key] };
  } );

  invokeMutation( {
    graphql,
    params,
    customHeaders,
    variables: {
      moduleParams,
      contentId
    }
  } );
};

/**
 * Property type definitions
  *
  * @typedef AnonymousOrderDetailStatus
  * @type {object}
  * @property {string} title - Sets the title
  * @property {string} subTitle - sets the sub title
  * @property {object} email - sets the email input field properties
  * @property {object} orderNumber - sets the order number input field properties
  * @property {object} buttonAction - sets the submit button action and label
  * @property {array} messages - Sets the response messages
  * @property {string} componentKey - Sets the componentKey
  * @property {string} id - Sets the id
  * @property {bool} loading - Sets the loading status
  * @property {function} invokeMutation Sets the invokeMutation function
  */
const propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string,
  email: PropTypes.object,
  orderNumber: PropTypes.object,
  buttonAction: PropTypes.object,
  messages: PropTypes.array,
  componentKey: PropTypes.string,
  id: PropTypes.string,
  loading: PropTypes.bool,
  invokeMutation: PropTypes.func
};

AnonymousOrderDetailStatus.propTypes = propTypes;

export default AnonymousOrderDetailStatus;
